import { ResolutionString } from 'charting_library';

import { ECexName } from 'web3';

export interface CexTimeframesCapability {
  default: ResolutionString;
  supported: ResolutionString[];
}

export type CexOrderbookPrecisionMode =
  | 'pair-decimals'
  | 'pair-decimals-single'
  | 'static-options'
  | 'unsupported';

export interface CexOrderbookPrecisionCapability {
  mode: CexOrderbookPrecisionMode;
  description: string;
  options?: string[];
}

export interface CexOrderbookCapability {
  defaultDepth: number;
  depthOptions: number[];
  maxDepth?: number;
  precision: CexOrderbookPrecisionCapability;
}

export interface CexTradesCapability {
  defaultLimit: number;
  limitOptions: number[];
  maxLimit?: number;
}

export interface CexHistoryCapability {
  paginationLimit: number;
  maxLimit: number;
}

export interface CexCapabilities {
  exchange: ECexName;
  timeframes: CexTimeframesCapability;
  orderbook: CexOrderbookCapability;
  trades: CexTradesCapability;
  history: CexHistoryCapability;
}

// NOTE: Trading UI currently works with IChartTimeframe union in src/types/charts.ts,
// which limits the available buckets to a small subset of minutes/hours/days/weeks.
// Expanding beyond this list requires updating that union, every chart utils mapping,
// and the metrics API (see ApiCharts.getCexCharts + trading position sockets).
// TODO: Extend timeframe support once the backend + chart contexts accept more granular ranges.
const DEFAULT_TIMEFRAMES: ResolutionString[] = [
  '1',
  '5',
  '15',
  '60',
  '240',
  '1D',
] as ResolutionString[];

export const CEX_EXCHANGE_TIMEFRAMES: Record<ECexName, ResolutionString[]> = {
  // Keep in sync with DEFAULT_TIMEFRAMES / IChartTimeframe constraints.
  [ECexName.binance]: [
    '1S',
    '1',
    '3',
    '5',
    '15',
    '30',
    '60',
    '120',
    '240',
    '360',
    '480',
    '720',
    '1D',
    '3D',
    '1W',
    '1M',
  ] as ResolutionString[],
  [ECexName.gate]: [
    '1S',
    '10S',
    '30S',
    '1',
    '3',
    '5',
    '15',
    '30',
    '60',
    '120',
    '240',
    '360',
    '480',
    '720',
    '1D',
    '2D',
    '3D',
    '5D',
    '1W',
    '1M',
  ] as ResolutionString[],
  [ECexName.kucoin]: ['1S', '1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.mexc]: ['1S', '1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.bingx]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.bitget]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.okx]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.bybit]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.bitmart]: [...DEFAULT_TIMEFRAMES],
  [ECexName.xt]: [...DEFAULT_TIMEFRAMES],
  [ECexName.lbank]: [...DEFAULT_TIMEFRAMES],
  [ECexName.htx]: ['1S', '1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.whitebit]: [...DEFAULT_TIMEFRAMES],
  [ECexName.coinbase]: ['1', '5', '15', '60', '360', '1D'] as ResolutionString[],
  [ECexName.cryptocom]: ['1', '5', '15', '30', '60', '240', '1D', '1W'] as ResolutionString[],
  [ECexName.upbit]: [
    '1',
    '3',
    '5',
    '15',
    '30',
    '60',
    '240',
    '1D',
    '1W',
    '1M',
  ] as ResolutionString[],
  [ECexName.bitstamp]: [
    '1',
    '3',
    '5',
    '15',
    '30',
    '60',
    '120',
    '240',
    '360',
    '720',
    '1D',
    '3D',
  ] as ResolutionString[],
  [ECexName.coinstore]: [...DEFAULT_TIMEFRAMES],
  [ECexName.indodax]: [...DEFAULT_TIMEFRAMES],
  [ECexName.hotcoin]: [...DEFAULT_TIMEFRAMES],
  [ECexName.kraken]: [...DEFAULT_TIMEFRAMES],
  // weex is Bitget-derived (SPOT); mirror Bitget's supported timeframes.
  [ECexName.weex]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  // biconomy (CoinEx/ViaBTC-style SPOT) supports 1/5/15/30min, hour, 4hour, day, week.
  [ECexName.biconomy]: ['1', '5', '15', '60', '240', '1D'] as ResolutionString[],
  [ECexName.coinw]: [...DEFAULT_TIMEFRAMES],
};

const PRECISION_DYNAMIC: CexOrderbookPrecisionCapability = {
  mode: 'pair-decimals',
  description:
    'Tick size derived from pair-specific precision. UI should present the most granular decimal options returned by the exchange.',
};

const PRECISION_SINGLE: CexOrderbookPrecisionCapability = {
  mode: 'pair-decimals-single',
  description:
    'Exchange exposes only a single tick size per pair. UI must use the provided decimal value without alternatives.',
};

const PRECISION_UNSUPPORTED: CexOrderbookPrecisionCapability = {
  mode: 'unsupported',
  description: 'Exchange API does not expose adjustable order book precision.',
};

const DEFAULT_ORDERBOOK_CAPABILITY: CexOrderbookCapability = {
  defaultDepth: 50,
  depthOptions: [20, 50, 100, 200],
  maxDepth: 1000,
  precision: PRECISION_DYNAMIC,
};

const DEFAULT_TRADES_CAPABILITY: CexTradesCapability = {
  defaultLimit: 50,
  limitOptions: [20, 50, 100, 200],
  maxLimit: 1000,
};

const DEFAULT_HISTORY_CAPABILITY: CexHistoryCapability = {
  paginationLimit: 500,
  maxLimit: 1000,
};

const clonePrecision = (
  precision: CexOrderbookPrecisionCapability,
): CexOrderbookPrecisionCapability => ({
  ...precision,
  options: precision.options ? [...precision.options] : undefined,
});

type CapabilitiesOverrides = Partial<{
  timeframes: Partial<CexTimeframesCapability> & { default?: ResolutionString };
  orderbook: Partial<CexOrderbookCapability>;
  trades: Partial<CexTradesCapability>;
  history: Partial<CexHistoryCapability>;
}>;

const withDefaults = (
  exchange: ECexName,
  overrides: CapabilitiesOverrides = {},
): CexCapabilities => {
  const supported =
    overrides.timeframes?.supported ?? CEX_EXCHANGE_TIMEFRAMES[exchange] ?? DEFAULT_TIMEFRAMES;
  const defaultResolution =
    overrides.timeframes?.default ??
    (supported.includes('1' as ResolutionString)
      ? ('1' as ResolutionString)
      : (supported[0] ?? '1'));

  const orderbook: CexOrderbookCapability = {
    defaultDepth: overrides.orderbook?.defaultDepth ?? DEFAULT_ORDERBOOK_CAPABILITY.defaultDepth,
    depthOptions: [
      ...(overrides.orderbook?.depthOptions ?? DEFAULT_ORDERBOOK_CAPABILITY.depthOptions),
    ],
    maxDepth: overrides.orderbook?.maxDepth ?? DEFAULT_ORDERBOOK_CAPABILITY.maxDepth,
    precision: clonePrecision(
      overrides.orderbook?.precision ?? DEFAULT_ORDERBOOK_CAPABILITY.precision,
    ),
  };

  const trades: CexTradesCapability = {
    defaultLimit: overrides.trades?.defaultLimit ?? DEFAULT_TRADES_CAPABILITY.defaultLimit,
    limitOptions: [...(overrides.trades?.limitOptions ?? DEFAULT_TRADES_CAPABILITY.limitOptions)],
    maxLimit: overrides.trades?.maxLimit ?? DEFAULT_TRADES_CAPABILITY.maxLimit,
  };

  const history: CexHistoryCapability = {
    paginationLimit:
      overrides.history?.paginationLimit ?? DEFAULT_HISTORY_CAPABILITY.paginationLimit,
    maxLimit: overrides.history?.maxLimit ?? DEFAULT_HISTORY_CAPABILITY.maxLimit,
  };

  return {
    exchange,
    timeframes: {
      default: defaultResolution,
      supported: [...supported],
    },
    orderbook,
    trades,
    history,
  };
};

const CAPABILITIES: Record<ECexName, CexCapabilities> = {
  [ECexName.binance]: withDefaults(ECexName.binance, {
    orderbook: {
      defaultDepth: 20,
      depthOptions: [5, 10, 20, 50, 100], // UI display options
      maxDepth: 5000, // REST API /depth supports up to 5000 levels
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200, 500, 1000],
      maxLimit: 1000,
    },
  }),
  [ECexName.gate]: withDefaults(ECexName.gate, {
    orderbook: {
      defaultDepth: 20,
      depthOptions: [10, 20, 50, 100], // UI display options
      maxDepth: 100, // WebSocket spot.order_book_update max depth
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 500,
    },
  }),
  [ECexName.kucoin]: withDefaults(ECexName.kucoin, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200],
      maxDepth: 400,
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200, 500],
      maxLimit: 500,
    },
    history: {
      paginationLimit: 1500, // KuCoin supports up to 1500 candles per request
      maxLimit: 1500,
    },
  }),
  [ECexName.mexc]: withDefaults(ECexName.mexc, {
    orderbook: {
      defaultDepth: 100,
      depthOptions: [20, 50, 100, 200, 500],
      maxDepth: 1000,
    },
  }),
  [ECexName.bitget]: withDefaults(ECexName.bitget, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200, 400],
      maxDepth: 400,
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 400,
    },
  }),
  [ECexName.okx]: withDefaults(ECexName.okx, {
    orderbook: {
      defaultDepth: 40,
      depthOptions: [5, 10, 20, 30, 40, 50],
      maxDepth: 400,
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 500,
    },
  }),
  [ECexName.bybit]: withDefaults(ECexName.bybit, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [25, 50, 100, 200],
      maxDepth: 200,
    },
  }),
  [ECexName.bingx]: withDefaults(ECexName.bingx, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200],
      maxDepth: 200,
    },
  }),
  [ECexName.bitmart]: withDefaults(ECexName.bitmart),
  [ECexName.xt]: withDefaults(ECexName.xt),
  [ECexName.lbank]: withDefaults(ECexName.lbank, {
    orderbook: {
      defaultDepth: 60,
      depthOptions: [20, 40, 60, 80, 100],
      maxDepth: 100,
      precision: PRECISION_SINGLE,
    },
  }),
  [ECexName.htx]: withDefaults(ECexName.htx, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200],
      maxDepth: 150,
    },
  }),
  [ECexName.whitebit]: withDefaults(ECexName.whitebit),
  [ECexName.coinbase]: withDefaults(ECexName.coinbase, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200],
      maxDepth: 1000,
    },
    trades: {
      defaultLimit: 50,
      limitOptions: [20, 50, 100],
      maxLimit: 100,
    },
    history: {
      paginationLimit: 300,
      maxLimit: 300,
    },
  }),
  [ECexName.cryptocom]: withDefaults(ECexName.cryptocom, {
    orderbook: {
      defaultDepth: 20,
      depthOptions: [10, 20],
      maxDepth: 20,
    },
    trades: {
      defaultLimit: 50,
      limitOptions: [20, 50],
      maxLimit: 50,
    },
    history: {
      paginationLimit: 300,
      maxLimit: 300,
    },
  }),
  [ECexName.upbit]: withDefaults(ECexName.upbit, {
    orderbook: {
      defaultDepth: 15,
      depthOptions: [15, 30],
      maxDepth: 30,
      precision: PRECISION_SINGLE,
    },
    trades: {
      defaultLimit: 50,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 200,
    },
    history: {
      paginationLimit: 200,
      maxLimit: 300,
    },
  }),
  [ECexName.bitstamp]: withDefaults(ECexName.bitstamp, {
    orderbook: {
      defaultDepth: 20,
      depthOptions: [10, 20, 50, 100],
      maxDepth: 100,
      precision: PRECISION_SINGLE,
    },
    trades: {
      defaultLimit: 50,
      limitOptions: [20, 50],
      maxLimit: 50,
    },
    history: {
      paginationLimit: 1000,
      maxLimit: 1000,
    },
  }),
  [ECexName.coinstore]: withDefaults(ECexName.coinstore, {
    orderbook: {
      precision: PRECISION_SINGLE,
    },
  }),
  [ECexName.indodax]: withDefaults(ECexName.indodax, {
    orderbook: {
      defaultDepth: 25,
      depthOptions: [10, 25, 50, 100],
      maxDepth: 200,
      precision: PRECISION_UNSUPPORTED,
    },
  }),
  [ECexName.hotcoin]: withDefaults(ECexName.hotcoin, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 150],
      maxDepth: 200,
    },
  }),
  [ECexName.kraken]: withDefaults(ECexName.kraken, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [10, 20, 50, 100],
      maxDepth: 500,
    },
  }),
  // weex is Bitget-derived (SPOT); mirror Bitget's capability profile.
  [ECexName.weex]: withDefaults(ECexName.weex, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200, 400],
      maxDepth: 400,
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 400,
    },
  }),
  // biconomy is CoinEx/ViaBTC-style (SPOT); mirror the Bitget/WEEX capability profile.
  [ECexName.biconomy]: withDefaults(ECexName.biconomy, {
    orderbook: {
      defaultDepth: 50,
      depthOptions: [20, 50, 100, 200, 400],
      maxDepth: 400,
    },
    trades: {
      defaultLimit: 100,
      limitOptions: [20, 50, 100, 200],
      maxLimit: 400,
    },
  }),
  [ECexName.coinw]: withDefaults(ECexName.coinw),
};

const cloneCapabilities = (capabilities: CexCapabilities): CexCapabilities => ({
  exchange: capabilities.exchange,
  timeframes: {
    default: capabilities.timeframes.default,
    supported: [...capabilities.timeframes.supported],
  },
  orderbook: {
    defaultDepth: capabilities.orderbook.defaultDepth,
    depthOptions: [...capabilities.orderbook.depthOptions],
    maxDepth: capabilities.orderbook.maxDepth,
    precision: clonePrecision(capabilities.orderbook.precision),
  },
  trades: {
    defaultLimit: capabilities.trades.defaultLimit,
    limitOptions: [...capabilities.trades.limitOptions],
    maxLimit: capabilities.trades.maxLimit,
  },
  history: {
    paginationLimit: capabilities.history.paginationLimit,
    maxLimit: capabilities.history.maxLimit,
  },
});

export const getStaticCexCapabilities = (exchange: ECexName): CexCapabilities => {
  const capabilities = CAPABILITIES[exchange];

  if (!capabilities) {
    throw new Error(`[CEX] Capabilities for exchange "${exchange}" are not defined`);
  }

  return cloneCapabilities(capabilities);
};

export const CEX_CAPABILITIES_MAP: Record<ECexName, CexCapabilities> = CAPABILITIES;
